import { PageWrap } from 'layouts'
import React, { useRef } from 'react'
import {
  Box,
  Flex,
  FlexProps,
  Heading,
  Skeleton,
  useColorModeValue,
  useMediaQuery,
  Text
} from '@chakra-ui/react'

import { Search } from 'components/SearchEngine'
import Breadcrumbs from 'components/Breadcrumbs'
import { useScrollToTheTop } from 'hooks'
import { useInView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'

type SearchWrapperProps = {
  loading?: boolean
  title: string
  showDescription?: boolean
  isResult?: boolean
  subHeader?: string
  heading?: string
  children?: (ref: React.RefObject<HTMLDivElement>) => JSX.Element
} & FlexProps

const excludedPaths = ['/auth', '/auth/', '/']

const SearchWrapper: React.FC<SearchWrapperProps> = ({
  title,
  loading,
  children,
  heading,
  showDescription,
  isResult,
  subHeader,
  ...rest
}) => {
  const headingColor = useColorModeValue('gray.700', 'gray.300')
  const resultsRef = useRef<HTMLDivElement>(null)
  const [isTabletOrMobile] = useMediaQuery(`(max-width: ${!isResult ? '50em' : '70em'})`)

  const [ref] = useInView({
    threshold: 0,
    initialInView: true
  })

  const { pathname } = useLocation()
  const shouldShowSearch = !excludedPaths.includes(pathname)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  useScrollToTheTop(resultsRef)
  return (
    <PageWrap
      title={title}
      display="flex"
      align="flex-start"
      flexDir={!isTabletOrMobile && shouldShowSearch ? 'row' : 'column'}
      {...rest}
    >
      <Flex
        {...(!isTabletOrMobile && shouldShowSearch
          ? {
              position: 'sticky',
              top: 100,
              py: 0,
              maxWidth: 348
            }
          : {})}
        zIndex={!isTabletOrMobile && shouldShowSearch ? 43 : 45}
        ref={searchContainerRef}
        align="flex-start"
      >
        <Search
          {...(!isTabletOrMobile && shouldShowSearch
            ? {
                position: 'sticky',
                float: 'left',
                top: 100,
                py: 0,
                maxWidth: 348
              }
            : {})}
          isResult={isResult}
          shouldShowSearch={shouldShowSearch}
          showDescription={showDescription}
          flex={2.2}
        />
      </Flex>

      {!showDescription && isTabletOrMobile && (
        <Flex transform="translateY(-3.9rem)" ref={resultsRef} />
      )}

      <Flex
        zIndex={44}
        pl={isTabletOrMobile || !isResult ? 0 : 10}
        ml={isTabletOrMobile || !isResult ? 0 : 6}
        flex={isResult && !isTabletOrMobile ? 1 : undefined}
        direction="column"
      >
        <Box>
          <Box>
            <Flex pb={4}>
              <Breadcrumbs />
            </Flex>

            {heading && (
              <Skeleton maxW="fit-content" isLoaded={!loading}>
                <Heading as="h4" fontWeight="extrabold" color={headingColor}>
                  {heading}
                </Heading>
                <Text fontSize="xs">{subHeader}</Text>
              </Skeleton>
            )}
          </Box>

          {children?.(ref as any)}
        </Box>
      </Flex>
    </PageWrap>
  )
}
SearchWrapper.defaultProps = {
  showDescription: false
}
export default React.memo(SearchWrapper)
